import { Model, type RectangularPlate } from 'metalworks-domain'

import { DistanceDimensionInput } from '@salsita-ai/platform-project-sdk/react'
import { EntityPanel, PropertyContainer } from '@salsita-ai/platform-project-sdk/ui'

import { CustomGenericDimensionInput } from '../fields/CustomGenericDimensionInput'
import {
  BendProperties,
  FinishDirectionProperty,
  FinishProperty,
  HandingProperty,
  HasBendProperty,
  MaterialSwatchProperty,
  NonStandardMaterialWarningWrapper,
  PowderCoatingProperty,
  PowderCoatingWarningWrapper,
  ThicknessProperty,
  cornerRadiusFixableViolations,
} from './shared-property-page-components'

const legthFixableViolations = ['cornerRadius.genericDimension.tooLarge']

interface Props {
  id: RectangularPlate['id']
  standard: boolean
  hasFinishDirection: boolean
  powderCoating: boolean
  $bend: Model.CustomBend | null
}

export const RectangularPlatePropertyPage = (props: Props) => {
  const { id, standard, hasFinishDirection, powderCoating, $bend } = props

  return (
    <EntityPanel.Layout
      additionalAction={null}
      header={
        <EntityPanel.Header
          title="Rectangular Plate"
          description="Customise your rectangular plate. You can change the length, width, corner radius and thickness of your plate and select your preferred material."
          previewImage={undefined}
          primaryButton={null}
          secondaryButton={null}
        />
      }
    >
      <PropertyContainer.Group spacing="large">
        <MaterialSwatchProperty id={id} />
        <PowderCoatingProperty id={id} />
        <PowderCoatingWarningWrapper powderCoating={powderCoating} />
        <FinishProperty id={id} />
        {hasFinishDirection && <FinishDirectionProperty id={id} />}
        <HandingProperty id={id} />
        <ThicknessProperty id={id} />
        <NonStandardMaterialWarningWrapper standard={standard} />

        <HasBendProperty id={id} />
        {$bend ? (
          <>
            <BendProperties bendId={$bend.id} />
            {$bend.orientation === Model.BendOrientation.Horizontal ? (
              <PropertyContainer.Item title="Width">
                <DistanceDimensionInput
                  entityId={id}
                  instanceName="horizontalLength"
                  fixableViolations={legthFixableViolations}
                />
              </PropertyContainer.Item>
            ) : (
              <PropertyContainer.Item title="Height">
                <DistanceDimensionInput
                  entityId={id}
                  instanceName="verticalLength"
                  fixableViolations={legthFixableViolations}
                />
              </PropertyContainer.Item>
            )}
          </>
        ) : (
          <>
            <PropertyContainer.Item title="Width">
              <DistanceDimensionInput
                entityId={id}
                instanceName="horizontalLength"
                fixableViolations={legthFixableViolations}
              />
            </PropertyContainer.Item>
            <PropertyContainer.Item title="Height">
              <DistanceDimensionInput
                entityId={id}
                instanceName="verticalLength"
                fixableViolations={legthFixableViolations}
              />
            </PropertyContainer.Item>
          </>
        )}

        <PropertyContainer.Item title="Corner Radius">
          <CustomGenericDimensionInput
            entityId={id}
            instanceName="cornerRadius"
            fixableViolations={cornerRadiusFixableViolations}
          />
        </PropertyContainer.Item>
      </PropertyContainer.Group>
    </EntityPanel.Layout>
  )
}
